import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import {
  createDairyFootprintCalculationSummary,
  createFootprintCalculationSummary,
  isPercentageComplete,
  sumCropYieldTonnesAndHectares,
  sumFootprintCalculationSummaries,
  useGetFarmVerificationSummary,
  useGetFootprintingCalculatedCropFootprints,
  useGetFootprintingCalculatedDairyFootprints,
  useGetFootprintingCalculatedFarmFootprints,
  useGetFootprintingCropFootprints,
  useGetFootprintingDairyFootprints,
  useGetFootprintingFarmFootprints,
  useGetPracticeCrops,
  useTranslation,
} from "utils";

import { COUNTRY_CODE, DEFAULT_DAIRY_FOOTPRINT_KGCO2EQ, KG_PER_TONNE, REGIONAL_COMPARISON_CROP_MAPPINGS } from "consts";

import { Flex, Message, PageSpin } from "components";

import { FootprintingPDFExportPage } from "pages/admin/verifications/components/footprinting/pdf-pages/FootprintingExportPDFPage";

interface FootprintingPdfExportProps {
  farmId: string;
  year: number;
  availableYears: number[];
  farmProgressQueries: ReturnType<typeof useGetFootprintingFarmFootprints>;
  cropProgressQueries: ReturnType<typeof useGetFootprintingCropFootprints>;
  dairyProgressQueries: ReturnType<typeof useGetFootprintingDairyFootprints>;
  cropDetails: ReturnType<typeof useGetPracticeCrops>["data"];
  isCropDetailsLoading: boolean;
  onClose: () => void;
}

const PRINT_RENDER_DELAY_MS = 500;
const COVER_PHOTO_SRC = "/images/footprinting/cover-photo.png";

const toPdfSafeUppercase = (value: string, language = "en"): string => value.toLocaleUpperCase(language);

const FootprintingPdfExport = ({
  farmId,
  year,
  availableYears,
  farmProgressQueries,
  cropProgressQueries,
  dairyProgressQueries,
  cropDetails,
  isCropDetailsLoading,
  onClose,
}: FootprintingPdfExportProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [coverPhotoDataUri, setCoverPhotoDataUri] = useState<string | null>(null);

  useEffect(() => {
    // Pre-convert the cover photo to a base64 data URI before printing.
    // Firefox on macOS re-fetches external image URLs during PDF generation (not
    // just preview), and drops images it cannot resolve — embedding as base64
    // guarantees the image survives the PDF rasterization step.
    fetch(COVER_PHOTO_SRC)
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      )
      .then((dataUri) => setCoverPhotoDataUri(dataUri))
      .catch(() => setCoverPhotoDataUri(COVER_PHOTO_SRC));
  }, []);
  const { t, i18n } = useTranslation("footprinting");
  const carbonFootprintReportRaw = String(t("pdfExport.carbonFootprintReport"));
  const carbonFootprintReportLabel = useMemo(() => toPdfSafeUppercase(carbonFootprintReportRaw, i18n.language), [carbonFootprintReportRaw, i18n.language]);

  const farmVerificationQuery = useGetFarmVerificationSummary({ farmId });
  const farmDetails = farmVerificationQuery.data?.farm;
  const farmCalculationQueries = useGetFootprintingCalculatedFarmFootprints(farmId, availableYears);

  const dairyCalculationQueries = useGetFootprintingCalculatedDairyFootprints(farmId, availableYears);

  const completedCropProgressQueries = cropProgressQueries.filter((cpq) => cpq.data && isPercentageComplete(cpq.data.completionPct));
  const completedCropProgressQueriesForYear = completedCropProgressQueries.filter((cpq) => cpq.data?.year === year);

  const completedCropCalculationQueries = useGetFootprintingCalculatedCropFootprints(
    farmId,
    availableYears,
    completedCropProgressQueries.map((cpq) => cpq.data?.cropId).filter((id) => id !== undefined)
  );

  const isLoading =
    farmVerificationQuery.isLoading ||
    isCropDetailsLoading ||
    farmProgressQueries.some((q) => q.isLoading) ||
    farmCalculationQueries.some((q) => q.isLoading) ||
    dairyProgressQueries.some((q) => q.isLoading) ||
    dairyCalculationQueries.some((q) => q.isLoading) ||
    cropProgressQueries.some((q) => q.isLoading) ||
    completedCropCalculationQueries.some((q) => q.isLoading);

  const cropCalculationSummaries = useMemo(() => {
    if (isLoading || !farmDetails) return [];

    const results = completedCropProgressQueriesForYear
      .map((cpq) => {
        const ccq = completedCropCalculationQueries.find((ccq) => ccq.data?.cropId === cpq.data?.cropId && cpq.data?.year === ccq.data?.year);
        const fcq = farmCalculationQueries.find((fcq) => fcq.data?.year === cpq.data?.year);

        if (!cpq.data || !ccq?.data || !fcq?.data) return null;

        const cropInfo = cropDetails?.find((cd) => cd.id === cpq.data?.cropId);
        const summary = createFootprintCalculationSummary(farmDetails, fcq.data, ccq.data, cpq.data.footprint);

        if (!summary) return null;

        const totalHectares = cpq.data.footprint?.totalFieldHectares ?? 0;

        const normalizedYieldTonnes = cpq.data.footprint?.normalizedWeightTs ?? null;

        return {
          cropId: cpq.data.cropId,
          cropName: cropInfo?.name ?? "Unknown Crop",
          summary,
          totalHectares,
          totalYieldTonnes: cpq.data.footprint?.totalWeightTs ?? 0,
          normalizedYieldTonnes,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    return results;
  }, [isLoading, farmDetails, completedCropProgressQueriesForYear, completedCropCalculationQueries, farmCalculationQueries, cropDetails]);

  const dairyCalculationSummary = useMemo(() => {
    const dairyProgressQuery = dairyProgressQueries.find((dpq) => dpq.data?.year === year);
    if (!farmDetails || !dairyProgressQuery) return null;

    const dcq = dairyCalculationQueries.find((dcq) => dcq.data?.year === dairyProgressQuery.data?.year);
    const fcq = farmCalculationQueries.find((fcq) => fcq.data?.year === dairyProgressQuery.data?.year);

    return createDairyFootprintCalculationSummary(farmDetails, fcq?.data, dcq?.data, dairyProgressQuery.data);
  }, [dairyProgressQueries, farmDetails, dairyCalculationQueries, farmCalculationQueries, year]);

  const farmSummary = useMemo(() => {
    if (!farmDetails) return null;

    const { totalYieldTonnes, totalFootprintedHectares } = sumCropYieldTonnesAndHectares(
      completedCropProgressQueriesForYear.filter((cpq) => cpq.data !== undefined).map((cpq) => cpq.data?.footprint)
    );

    const summary = sumFootprintCalculationSummaries(
      cropCalculationSummaries.map((ccs) => ccs.summary),
      KG_PER_TONNE * totalYieldTonnes,
      totalFootprintedHectares
    );

    const totalHectares = cropCalculationSummaries.reduce((sum, item) => sum + item.totalHectares, 0);

    return {
      summary,
      totalHectares,
      commodityCount: cropCalculationSummaries.length,
    };
  }, [cropCalculationSummaries, completedCropProgressQueriesForYear, farmDetails]);

  const hasDairy = dairyCalculationSummary !== null;
  const numberedTotal = 1 + (hasDairy ? 1 : 0) + cropCalculationSummaries.length;

  useEffect(() => {
    const hasAssessmentsToGenerate = cropCalculationSummaries.length > 0 || dairyCalculationSummary !== null;
    const queriesSettled = !isLoading && !isGenerating && !hasGenerated && coverPhotoDataUri !== null;
    const shouldGenerate = hasAssessmentsToGenerate && queriesSettled;

    if (queriesSettled && !hasAssessmentsToGenerate) {
      Message({ content: t("pdfExport.noDataError"), icon: "error" });
      onClose();
      return;
    }

    if (!shouldGenerate) return;

    setIsGenerating(true);

    const handleAfterPrint = () => {
      window.removeEventListener("afterprint", handleAfterPrint);
      setHasGenerated(true);
      setIsGenerating(false);
      onClose();
    };
    window.addEventListener("afterprint", handleAfterPrint);

    // Fire-and-forget: this setTimeout must NOT be cleared on effect re-run,
    // otherwise the setIsGenerating(true) above would cancel print() before it fires.
    setTimeout(() => {
      try {
        window.print();
      } catch {
        window.removeEventListener("afterprint", handleAfterPrint);
        setIsGenerating(false);
        Message({ content: "Failed to open print dialog", icon: "error" });
        onClose();
      }
    }, PRINT_RENDER_DELAY_MS);
  }, [isLoading, isGenerating, hasGenerated, cropCalculationSummaries.length, dairyCalculationSummary, coverPhotoDataUri, onClose, t]);

  return (
    <>
      {(isLoading || isGenerating || coverPhotoDataUri === null) && (
        <Flex className="footprinting-pdf-export-loading-overlay" align="center" justify="center" vertical>
          <PageSpin />
          <span className="footprinting-pdf-export-loading-text">{isGenerating ? "Generating PDF..." : "Loading data..."}</span>
        </Flex>
      )}

      {/* Portal to document.body so the print stylesheet can hide every other body child
          (the React app tree) and the export sits at the top of page 1 in print. */}
      {createPortal(
        <div className="footprinting-pdf-export-print-root">
          <div className="footprinting-pdf-export-container">
            <FootprintingPDFExportPage.CoverPage
              t={t}
              year={year}
              farmName={farmDetails?.name ?? ""}
              title={carbonFootprintReportRaw}
              subtitle={carbonFootprintReportLabel}
              date={new Date().toLocaleDateString()}
              coverPhotoSrc={coverPhotoDataUri ?? COVER_PHOTO_SRC}
            />

            {/* --- Farm Summary --- */}
            <FootprintingPDFExportPage.FarmPage
              title={carbonFootprintReportRaw}
              subtitle={carbonFootprintReportLabel}
              t={t}
              year={year}
              farmName={farmDetails?.name}
              farmSummary={farmSummary}
              currentPage={1}
              totalPages={numberedTotal}
              cropList={cropCalculationSummaries.map((ccs) => ({
                cropId: ccs.cropId,
                cropName: ccs.cropName,
                perHaCO2e: ccs.summary.overall.perHaCO2e,
                totalCO2e: ccs.summary.overall.totalCO2e,
                totalHectares: ccs.totalHectares,
              }))}
            />

            {/* Dairy Page */}
            {dairyCalculationSummary ? (
              <FootprintingPDFExportPage.DairyPage
                title={carbonFootprintReportRaw}
                subtitle={carbonFootprintReportLabel}
                t={t}
                year={year}
                farmName={farmDetails?.name}
                calculationSummary={dairyCalculationSummary}
                regionalComparisonValue={DEFAULT_DAIRY_FOOTPRINT_KGCO2EQ[(farmDetails?.country as COUNTRY_CODE) ?? ""]}
                currentPage={2}
                totalPages={numberedTotal}
              />
            ) : null}

            {/* Crop Pages */}
            {cropCalculationSummaries.map((item, index) => (
              <FootprintingPDFExportPage.CropPage
                t={t}
                title={carbonFootprintReportRaw}
                year={year}
                key={item.cropId}
                cropName={item.cropName}
                farmName={farmDetails.name ?? ""}
                normalizedYieldTonnes={item.normalizedYieldTonnes}
                calculationSummary={item.summary}
                subtitle={carbonFootprintReportLabel}
                totalHectares={item.totalHectares}
                totalYieldTonnes={item.totalYieldTonnes}
                regionalComparisonValue={REGIONAL_COMPARISON_CROP_MAPPINGS.find((c) => c.id === item.cropId)?.regionalCropComparisonValueCO2ekg}
                currentPage={1 + (hasDairy ? 1 : 0) + index + 1}
                totalPages={numberedTotal}
              />
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default FootprintingPdfExport;
